export const FULFILLMENT_MODES = ['customerDropoff', 'servicePickup', 'onsiteService'] as const;

export type FulfillmentMode = (typeof FULFILLMENT_MODES)[number];
export type FulfillmentPricingHint = 'quote' | 'included' | 'not_configured';
export type FulfillmentSource = 'providerProfile' | 'legacyFallback';

export interface ServiceFulfillmentPaymentConfig {
  cashOnly: true;
  onlinePaymentEnabled: false;
}

export interface ServiceFulfillmentModeConfig {
  enabled: boolean;
  requiresAddress?: boolean;
  instructions?: string;
  pricingHint?: FulfillmentPricingHint;
}

export type ServiceFulfillmentModesConfig = Partial<
  Record<FulfillmentMode, ServiceFulfillmentModeConfig>
>;

export interface ServiceProviderFulfillmentConfig<TTimestamp = unknown> {
  modes?: ServiceFulfillmentModesConfig;
  payment?: Partial<ServiceFulfillmentPaymentConfig> | null;
  updatedAt?: TTimestamp | null;
}

export interface RepairRequestFulfillmentAddressSnapshot {
  street?: string;
  city?: string;
  postalCode?: string;
  formatted?: string;
}

export interface RepairRequestFulfillmentSnapshot<TTimestamp = unknown> {
  selectedMode: FulfillmentMode;
  selectedModeLabel: string;
  selectedModeSource: FulfillmentSource;
  providerCapabilitiesSnapshot: {
    payment: ServiceFulfillmentPaymentConfig;
    mode: {
      requiresAddress: boolean;
      pricingHint?: FulfillmentPricingHint;
      instructions?: string;
    };
  };
  address?: RepairRequestFulfillmentAddressSnapshot;
  note?: string;
  preferredDate?: TTimestamp;
  createdAt: TTimestamp;
}

export interface FulfillmentProviderReadModel<TTimestamp = unknown> {
  serviceFulfillment?: ServiceProviderFulfillmentConfig<TTimestamp> | null;
}

export interface FulfillmentRequestReadModel<TTimestamp = unknown> {
  fulfillmentSnapshot?: RepairRequestFulfillmentSnapshot<TTimestamp> | unknown;
}

export interface ResolvedFulfillmentModeCapability {
  mode: FulfillmentMode;
  enabled: boolean;
  source: FulfillmentSource;
  requiresAddress: boolean;
  instructions?: string;
  pricingHint?: FulfillmentPricingHint;
}

export type ServiceFulfillmentCapabilitiesState = 'available' | 'empty' | 'invalid';

export interface ServiceFulfillmentCapabilities {
  state: ServiceFulfillmentCapabilitiesState;
  source: FulfillmentSource;
  modes: Record<FulfillmentMode, ResolvedFulfillmentModeCapability>;
  enabledModes: FulfillmentMode[];
  payment: ServiceFulfillmentPaymentConfig;
  issues: string[];
}

export type RepairRequestFulfillmentSnapshotState = 'resolved' | 'missing' | 'partial' | 'invalid';

export interface ResolvedRepairRequestFulfillmentSnapshot<TTimestamp = unknown> {
  state: RepairRequestFulfillmentSnapshotState;
  reason?: 'legacyMissing' | 'malformedSnapshot' | 'unknownMode' | 'partialSnapshot';
  source?: FulfillmentSource;
  selectedMode?: FulfillmentMode;
  selectedModeLabel?: string;
  requiresAddress?: boolean;
  payment?: ServiceFulfillmentPaymentConfig;
  address?: RepairRequestFulfillmentAddressSnapshot;
  note?: string;
  preferredDate?: TTimestamp;
  createdAt?: TTimestamp;
  snapshot?: RepairRequestFulfillmentSnapshot<TTimestamp>;
  issues: string[];
}

const DEFAULT_PAYMENT: ServiceFulfillmentPaymentConfig = {
  cashOnly: true,
  onlinePaymentEnabled: false,
};

const FULFILLMENT_MODE_LABELS: Record<FulfillmentMode, string> = {
  customerDropoff: 'Dostarczenie do serwisu',
  servicePickup: 'Odbiór od klienta',
  onsiteService: 'Naprawa u klienta',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

export const isFulfillmentMode = (value: unknown): value is FulfillmentMode =>
  typeof value === 'string' && (FULFILLMENT_MODES as readonly string[]).includes(value);

const isPricingHint = (value: unknown): value is FulfillmentPricingHint =>
  value === 'quote' || value === 'included' || value === 'not_configured';

export const getFulfillmentModeLabel = (mode: FulfillmentMode): string =>
  FULFILLMENT_MODE_LABELS[mode];

export const requiresFulfillmentAddress = (mode: FulfillmentMode): boolean =>
  mode === 'servicePickup' || mode === 'onsiteService';

export const getRepairRequestFulfillmentListLabel = (
  resolved: ResolvedRepairRequestFulfillmentSnapshot | null | undefined
): string | null => {
  if (!resolved?.selectedMode) {
    return null;
  }
  return getFulfillmentModeLabel(resolved.selectedMode);
};

export const formatRepairRequestFulfillmentAddress = (
  address?: RepairRequestFulfillmentAddressSnapshot
): string | null => {
  if (!address) {
    return null;
  }
  if (address.formatted) {
    return address.formatted;
  }
  const cityLine = [address.postalCode, address.city].filter(Boolean).join(' ');
  return [address.street, cityLine].filter(Boolean).join(', ') || null;
};

export const getRepairRequestFulfillmentPaymentLabel = (
  payment?: ServiceFulfillmentPaymentConfig
): string => {
  if (payment?.cashOnly === true && payment.onlinePaymentEnabled === false) {
    return 'Gotówka / rozliczenie bezpośrednie z serwisem';
  }
  return 'Rozliczenie bezpośrednie z serwisem';
};

export const getFulfillmentPricingHintLabel = (
  pricingHint?: FulfillmentPricingHint
): string | null => {
  if (pricingHint === 'quote') {
    return 'Koszt ustalany w wycenie';
  }
  if (pricingHint === 'included') {
    return 'Koszt ujęty w realizacji';
  }
  if (pricingHint === 'not_configured') {
    return 'Koszt dojazdu nie został skonfigurowany';
  }
  return null;
};

const normalizePayment = (value: unknown): ServiceFulfillmentPaymentConfig => {
  if (!isRecord(value)) {
    return DEFAULT_PAYMENT;
  }

  return {
    cashOnly: true,
    onlinePaymentEnabled: false,
  };
};

const createDisabledMode = (
  mode: FulfillmentMode,
  source: FulfillmentSource
): ResolvedFulfillmentModeCapability => ({
  mode,
  enabled: false,
  source,
  requiresAddress: requiresFulfillmentAddress(mode),
});

const createModeCapabilities = (
  source: FulfillmentSource
): Record<FulfillmentMode, ResolvedFulfillmentModeCapability> =>
  FULFILLMENT_MODES.reduce(
    (acc, mode) => {
      acc[mode] = createDisabledMode(mode, source);
      return acc;
    },
    {} as Record<FulfillmentMode, ResolvedFulfillmentModeCapability>
  );

export const resolveServiceFulfillmentCapabilities = (
  provider?: FulfillmentProviderReadModel | null
): ServiceFulfillmentCapabilities => {
  const config = provider?.serviceFulfillment;

  if (config == null) {
    const modes = createModeCapabilities('legacyFallback');
    modes.customerDropoff = {
      mode: 'customerDropoff',
      enabled: true,
      source: 'legacyFallback',
      requiresAddress: false,
    };

    return {
      state: 'available',
      source: 'legacyFallback',
      modes,
      enabledModes: ['customerDropoff'],
      payment: DEFAULT_PAYMENT,
      issues: ['missing-serviceFulfillment-legacy-fallback'],
    };
  }

  if (!isRecord(config)) {
    return {
      state: 'invalid',
      source: 'providerProfile',
      modes: createModeCapabilities('providerProfile'),
      enabledModes: [],
      payment: DEFAULT_PAYMENT,
      issues: ['malformed-serviceFulfillment'],
    };
  }

  const issues: string[] = [];
  const modes = createModeCapabilities('providerProfile');
  const rawModes = isRecord(config.modes) ? config.modes : null;

  if (!rawModes) {
    issues.push('missing-or-malformed-modes');
  } else {
    Object.keys(rawModes).forEach((modeKey) => {
      if (!isFulfillmentMode(modeKey)) {
        issues.push(`unknown-mode:${modeKey}`);
      }
    });

    FULFILLMENT_MODES.forEach((mode) => {
      const rawModeConfig = rawModes[mode];
      if (!isRecord(rawModeConfig)) {
        return;
      }

      const enabled = rawModeConfig.enabled === true;
      const capability: ResolvedFulfillmentModeCapability = {
        mode,
        enabled,
        source: 'providerProfile',
        requiresAddress: requiresFulfillmentAddress(mode),
      };

      if (typeof rawModeConfig.instructions === 'string' && rawModeConfig.instructions.trim()) {
        capability.instructions = rawModeConfig.instructions.trim();
      }
      if (isPricingHint(rawModeConfig.pricingHint)) {
        capability.pricingHint = rawModeConfig.pricingHint;
      }

      modes[mode] = capability;
    });
  }

  const enabledModes = FULFILLMENT_MODES.filter((mode) => modes[mode].enabled);

  return {
    state: enabledModes.length > 0 ? 'available' : 'empty',
    source: 'providerProfile',
    modes,
    enabledModes,
    payment: normalizePayment(config.payment),
    issues,
  };
};

export const getEnabledFulfillmentModes = (
  provider?: FulfillmentProviderReadModel | null
): FulfillmentMode[] => resolveServiceFulfillmentCapabilities(provider).enabledModes;

export const isFulfillmentModeEnabled = (
  provider: FulfillmentProviderReadModel | null | undefined,
  mode: unknown
): boolean =>
  isFulfillmentMode(mode) && resolveServiceFulfillmentCapabilities(provider).modes[mode].enabled;

const normalizeAddressSnapshot = (
  value: unknown
): RepairRequestFulfillmentAddressSnapshot | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const address: RepairRequestFulfillmentAddressSnapshot = {};
  (['street', 'city', 'postalCode', 'formatted'] as const).forEach((key) => {
    const rawValue = value[key];
    if (typeof rawValue === 'string' && rawValue.trim()) {
      address[key] = rawValue.trim();
    }
  });
  return Object.keys(address).length > 0 ? address : undefined;
};

export const resolveRepairRequestFulfillmentSnapshot = <TTimestamp = unknown>(
  request?: FulfillmentRequestReadModel<TTimestamp> | null
): ResolvedRepairRequestFulfillmentSnapshot<TTimestamp> => {
  const rawSnapshot = request?.fulfillmentSnapshot;

  if (rawSnapshot == null) {
    return {
      state: 'missing',
      reason: 'legacyMissing',
      issues: ['missing-fulfillmentSnapshot'],
    };
  }

  if (!isRecord(rawSnapshot)) {
    return {
      state: 'invalid',
      reason: 'malformedSnapshot',
      issues: ['malformed-fulfillmentSnapshot'],
    };
  }

  const selectedMode = rawSnapshot.selectedMode;
  if (!isFulfillmentMode(selectedMode)) {
    return {
      state: 'invalid',
      reason: 'unknownMode',
      issues: ['unknown-selectedMode'],
    };
  }

  const issues: string[] = [];
  const capabilities = isRecord(rawSnapshot.providerCapabilitiesSnapshot)
    ? rawSnapshot.providerCapabilitiesSnapshot
    : null;
  const modeSnapshot = capabilities && isRecord(capabilities.mode) ? capabilities.mode : null;
  const source =
    rawSnapshot.selectedModeSource === 'providerProfile' ||
    rawSnapshot.selectedModeSource === 'legacyFallback'
      ? rawSnapshot.selectedModeSource
      : undefined;
  const selectedModeLabel =
    typeof rawSnapshot.selectedModeLabel === 'string' && rawSnapshot.selectedModeLabel.trim()
      ? rawSnapshot.selectedModeLabel.trim()
      : getFulfillmentModeLabel(selectedMode);
  const createdAt = rawSnapshot.createdAt as TTimestamp | undefined;

  if (!source) {
    issues.push('missing-selectedModeSource');
  }
  if (!capabilities) {
    issues.push('missing-providerCapabilitiesSnapshot');
  }
  if (!modeSnapshot) {
    issues.push('missing-mode-snapshot');
  }
  if (!createdAt) {
    issues.push('missing-createdAt');
  }

  const requiresAddress =
    typeof modeSnapshot?.requiresAddress === 'boolean'
      ? modeSnapshot.requiresAddress
      : requiresFulfillmentAddress(selectedMode);
  const address = normalizeAddressSnapshot(rawSnapshot.address);
  if (requiresAddress && !address) {
    issues.push('missing-address');
  }

  const resolved: ResolvedRepairRequestFulfillmentSnapshot<TTimestamp> = {
    state: issues.length > 0 ? 'partial' : 'resolved',
    reason: issues.length > 0 ? 'partialSnapshot' : undefined,
    source,
    selectedMode,
    selectedModeLabel,
    requiresAddress,
    payment: normalizePayment(capabilities?.payment),
    address,
    note: typeof rawSnapshot.note === 'string' ? rawSnapshot.note : undefined,
    preferredDate: rawSnapshot.preferredDate as TTimestamp | undefined,
    createdAt,
    issues,
  };

  if (resolved.state === 'resolved') {
    resolved.snapshot = rawSnapshot as unknown as RepairRequestFulfillmentSnapshot<TTimestamp>;
  }

  return resolved;
};
